"use client";
import { useState } from "react";
import { twMerge } from "tailwind-merge";
import { getEntityIcon } from "../../config/entities";
import type { LinkComponentType } from "../../index";
import type { DeviceType } from "../../types/DeviceTypes";
import { useAssetImage } from "../../utils/AssetImageProvider";
import type { CellEmphasis } from "../DataTable/DataTable.types";
import { Icon, type IconName } from "../Icon";
import { Skeleton } from "../Skeleton";
import { TextLink } from "../TextLink";

export interface DeviceMetaDisplayProps {
  /** Device manufacturer name */
  manufacturer: string;

  /** Device model name */
  model: string;

  /** Device type for fallback icon (deprecated: use deviceTypeIconName instead) */
  deviceType?: DeviceType;

  /** Explicit device type icon name (preferred over deviceType) */
  deviceTypeIconName?: string;

  /** URL to manufacturer logo (optional) */
  logoUrl?: string;

  /** Layout variant - stacked (vertical) or inline (horizontal) */
  layout?: "stacked" | "inline";

  /** Size variant */
  size?: "xs" | "sm" | "md" | "lg";

  /** Link to device detail page (optional) */
  href?: string;

  /** Custom link component (e.g., Next.js Link) */
  LinkComponent?: LinkComponentType;

  /** Link variant for the manufacturer name */
  linkVariant?: "primary" | "secondary" | "muted" | "unstyled" | "default" | "brand";

  /** Show device type icon next to model name (stacked) or hide logo (inline) */
  showDeviceTypeIcon?: boolean;

  /** Show logo in stacked layout (can be hidden for compact view) */
  showLogo?: boolean;

  /** Text emphasis level - "strong"/"high" for bold, "normal" for regular, "low" for muted */
  emphasis?: CellEmphasis;

  /** Loading state */
  isLoading?: boolean;

  /** Additional classes */
  className?: string;

  /**
   * Entity identifier for PII masking (e.g., "device:device_abc123")
   * When provided, adds data-pii-* attributes for the Curtain extension
   */
  piiEntity?: string;
}

/**
 * DeviceMetaDisplay
 *
 * Reusable component for displaying device metadata (manufacturer, model, logo).
 * Supports stacked and inline layouts with size variants and optional linking.
 *
 * **Stacked layout**: Large icon/logo, manufacturer name above, device type icon + model below
 * **Inline layout**: Small icon + "Manufacturer Model" as single text string
 *
 * Use this component directly in cards, lists, or other layouts.
 * For DataTable cells, use DeviceMetaCell which wraps this component.
 *
 * @example
 * ```tsx
 * // Stacked layout (default)
 * <DeviceMetaDisplay
 *   manufacturer="Tesla"
 *   model="Powerwall 2"
 *   deviceType="battery"
 *   layout="stacked"
 *   size="md"
 *   logoUrl="https://..."
 *   href="/devices/123"
 * />
 *
 * // Inline layout
 * <DeviceMetaDisplay
 *   manufacturer="Tesla"
 *   model="Powerwall 2"
 *   deviceType="battery"
 *   layout="inline"
 *   size="sm"
 *   href="/devices/123"
 * />
 * ```
 */
export function DeviceMetaDisplay({
  manufacturer,
  model,
  deviceType,
  deviceTypeIconName,
  logoUrl: sourceLogoUrl,
  layout = "stacked",
  size = "md",
  href,
  LinkComponent,
  linkVariant = "unstyled",
  showDeviceTypeIcon = true,
  showLogo = true,
  emphasis = "normal",
  isLoading = false,
  className,
  piiEntity,
}: DeviceMetaDisplayProps) {
  const logoUrl = useAssetImage(sourceLogoUrl);
  const [failedImage, setFailedImage] = useState<string>();
  const imageError = failedImage !== undefined && failedImage === logoUrl;

  // Resolve icon name: prefer explicit deviceTypeIconName, fallback to deviceType mapping
  const resolvedIconName: IconName = (deviceTypeIconName ||
    (deviceType ? getDeviceTypeIconName(deviceType) : "DeviceMobile")) as IconName;

  // Size-based styling
  const sizes = {
    xs: {
      iconSize: 28,
      logoSize: "w-7 h-7",
      gap: "gap-1.5",
      textSize: "text-xs",
      secondaryTextSize: "text-xs",
      iconInTextSize: 12,
      skeletonIcon: 28,
      skeletonText: 80,
      skeletonSecondary: 60,
    },
    sm: {
      iconSize: 32,
      logoSize: "w-8 h-8",
      gap: "gap-2",
      textSize: "text-sm",
      secondaryTextSize: "text-xs",
      iconInTextSize: 14,
      skeletonIcon: 32,
      skeletonText: 96,
      skeletonSecondary: 72,
    },
    md: {
      iconSize: 32,
      logoSize: "w-9 h-9",
      gap: "gap-3",
      textSize: "text-base",
      secondaryTextSize: "text-sm",
      iconInTextSize: 16,
      skeletonIcon: 36,
      skeletonText: 128,
      skeletonSecondary: 96,
    },
    lg: {
      iconSize: 40,
      logoSize: "w-10 h-10",
      gap: "gap-3",
      textSize: "text-lg",
      secondaryTextSize: "text-base",
      iconInTextSize: 18,
      skeletonIcon: 40,
      skeletonText: 160,
      skeletonSecondary: 120,
    },
  };

  const sizeConfig = sizes[size];
  const showImage = logoUrl && !imageError && showLogo;

  // Map emphasis to font weight and color
  const emphasisClasses = {
    strong: "font-semibold text-text-primary",
    high: "font-semibold text-text-primary",
    normal: "text-text-primary",
    low: "text-text-secondary",
  };
  const emphasisClass = emphasisClasses[emphasis];

  // Render link wrapper
  const LinkWrapper = ({ children, className: linkClassName }: { children: React.ReactNode; className?: string }) => {
    if (!href) {
      return <span className={linkClassName}>{children}</span>;
    }

    if (LinkComponent) {
      return (
        <LinkComponent href={href} className={linkClassName}>
          {children}
        </LinkComponent>
      );
    }

    return (
      <TextLink
        href={href}
        variant={linkVariant}
        className={twMerge("hover:underline", linkClassName)}
        asButton={false}
      >
        {children}
      </TextLink>
    );
  };

  // Loading state
  if (isLoading) {
    if (layout === "inline") {
      return (
        <div className={twMerge("flex items-center", sizeConfig.gap, className)}>
          <Skeleton
            width={sizeConfig.skeletonIcon}
            height={sizeConfig.skeletonIcon}
            className="flex-shrink-0 rounded"
          />
          <Skeleton width={sizeConfig.skeletonText} height={sizeConfig.skeletonIcon * 0.6} />
        </div>
      );
    }

    return (
      <div className={twMerge("flex items-center", sizeConfig.gap, className)}>
        <Skeleton width={sizeConfig.skeletonIcon} height={sizeConfig.skeletonIcon} className="flex-shrink-0" />
        <div className="flex flex-col gap-1">
          <Skeleton width={sizeConfig.skeletonText} height={16} />
          <Skeleton width={sizeConfig.skeletonSecondary} height={12} />
        </div>
      </div>
    );
  }

  // Inline layout: Icon + "Manufacturer Model" as single text
  if (layout === "inline") {
    const displayText = `${manufacturer} ${model}`;

    return (
      <div className={twMerge("flex items-center", sizeConfig.gap, className)}>
        {/* Small icon */}
        <div
          className={twMerge(
            "flex-shrink-0 flex items-center justify-center rounded border border-border-muted",
            sizeConfig.logoSize
          )}
        >
          {showImage ? (
            <img
              src={logoUrl}
              alt={`${manufacturer} logo`}
              className="w-full h-full object-contain"
              onError={() => setFailedImage(logoUrl)}
            />
          ) : (
            <Icon name={resolvedIconName} size={sizeConfig.iconSize * 0.6} className="text-text-secondary" />
          )}
        </div>

        {/* Combined text */}
        <LinkWrapper
          className={twMerge(
            "truncate leading-normal hover:text-text-primary",
            emphasisClass,
            sizeConfig.textSize,
            href && "hover:underline hover:decoration-1 hover:underline-offset-4"
          )}
        >
          <span data-pii-type={piiEntity ? "device-name" : undefined} data-pii-entity={piiEntity}>
            {displayText}
          </span>
        </LinkWrapper>
      </div>
    );
  }

  // Stacked layout: Logo/icon, manufacturer name above, icon + model below
  return (
    <div className={twMerge("flex items-center justify-start", sizeConfig.gap, className)}>
      {/* Manufacturer logo or icon fallback */}
      {showLogo && (
        <div
          className={twMerge(
            "flex-shrink-0 flex items-center justify-center rounded border border-border-muted",
            sizeConfig.logoSize
          )}
        >
          {showImage ? (
            <img
              src={logoUrl}
              alt={`${manufacturer} logo`}
              className="w-full h-full object-contain"
              onError={() => setFailedImage(logoUrl)}
            />
          ) : (
            <Icon name={resolvedIconName} size={sizeConfig.iconSize * 0.6} className="text-text-secondary" />
          )}
        </div>
      )}

      {/* Device info */}
      <div className="flex flex-col items-start min-w-0">
        {/* Manufacturer name - linked if href provided */}
        <LinkWrapper
          className={twMerge(
            "truncate leading-normal hover:text-text-primary",
            emphasisClass,
            sizeConfig.textSize,
            href && "hover:underline hover:decoration-1 hover:underline-offset-4"
          )}
        >
          <span data-pii-type={piiEntity ? "device-name" : undefined} data-pii-entity={piiEntity}>
            {manufacturer}
          </span>
        </LinkWrapper>

        {/* Model name with device type icon */}
        <div
          className={twMerge(
            "flex items-center gap-1.5 text-text-secondary leading-normal",
            sizeConfig.secondaryTextSize
          )}
        >
          {showDeviceTypeIcon && (
            <Icon name={resolvedIconName} size={sizeConfig.iconInTextSize} className="flex-shrink-0" />
          )}
          <span className="truncate" data-pii-type={piiEntity ? "device-name" : undefined} data-pii-entity={piiEntity}>
            {model}
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * Helper function to get device type icon name
 */
function getDeviceTypeIconName(type: DeviceType): IconName {
  switch (type) {
    case "battery":
      return "BatteryHigh";
    case "charger":
      return "ChargingStation";
    case "inverter":
      return "Power";
    case "thermostat":
      return "ThermometerSimple";
    case "vehicle":
      return "CarSimple";
    case "water_heater":
      return "DropHalfBottom";
    case "meter":
      return "Gauge";
    case "sensor":
      return getEntityIcon("sensor");
    default:
      return "DeviceMobile";
  }
}
